import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { api } from "../lib/api";
import PageHeader from "../components/PageHeader";
import StatusPill from "../components/StatusPill";

const STATUSES = [
  { v: "", label: "All statuses" },
  { v: "in_stock", label: "In stock" },
  { v: "expiring_soon", label: "Expiring soon" },
  { v: "expired", label: "Expired" },
  { v: "shipped", label: "Shipped" },
];

export default function InventoryPage() {
  const [pallets, setPallets] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState({ status: "", customer_id: "", product_id: "", search: "" });
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    const params = {};
    if (filter.status) params.status = filter.status;
    if (filter.customer_id) params.customer_id = filter.customer_id;
    if (filter.product_id) params.product_id = filter.product_id;
    if (filter.search) params.search = filter.search;
    const { data } = await api.get("/pallets", { params });
    setPallets(data);
    setLoading(false);
  };

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get("/products").then((r) => setProducts(r.data));
  }, []);

  useEffect(() => {
    const t = setTimeout(fetchData, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [filter]);

  const counts = useMemo(() => {
    const c = { total: pallets.length, in_stock: 0, expiring_soon: 0, expired: 0, shipped: 0 };
    pallets.forEach((p) => (c[p.status] = (c[p.status] || 0) + 1));
    return c;
  }, [pallets]);

  return (
    <div data-testid="inventory-page">
      <PageHeader eyebrow="Warehouse" title="Inventory." />
      <div className="p-6 md:p-10 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { k: "total", l: "Total" },
            { k: "in_stock", l: "In stock" },
            { k: "expiring_soon", l: "Expiring" },
            { k: "expired", l: "Expired" },
            { k: "shipped", l: "Shipped" },
          ].map((x) => (
            <div key={x.k} className="mj-card py-4" data-testid={`inv-count-${x.k}`}>
              <div className="data-label mb-1">{x.l}</div>
              <div className="font-display text-3xl font-bold">{counts[x.k] || 0}</div>
            </div>
          ))}
        </div>

        <div className="mj-card">
          <div className="grid md:grid-cols-4 gap-3 mb-5">
            <div className="relative md:col-span-1">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"
              />
              <input
                placeholder="Pallet #, lot, product…"
                className="mj-input pl-9"
                value={filter.search}
                onChange={(e) => setFilter({ ...filter, search: e.target.value })}
                data-testid="inv-search-input"
              />
            </div>
            <select
              className="mj-input"
              value={filter.status}
              onChange={(e) => setFilter({ ...filter, status: e.target.value })}
              data-testid="inv-status-filter"
            >
              {STATUSES.map((s) => (
                <option key={s.v} value={s.v}>
                  {s.label}
                </option>
              ))}
            </select>
            <select
              className="mj-input"
              value={filter.customer_id}
              onChange={(e) => setFilter({ ...filter, customer_id: e.target.value })}
              data-testid="inv-customer-filter"
            >
              <option value="">All customers</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <select
              className="mj-input"
              value={filter.product_id}
              onChange={(e) => setFilter({ ...filter, product_id: e.target.value })}
              data-testid="inv-product-filter"
            >
              <option value="">All products</option>
              {products.map((p) => (
                <option key={p.id} value={p.id}>{p.sku} — {p.name}</option>
              ))}
            </select>
          </div>

          <div className="overflow-x-auto">
            <table className="mj-table" data-testid="inventory-table">
              <thead>
                <tr>
                  <th>Pallet #</th>
                  <th>Product</th>
                  <th>Lot</th>
                  <th>Qty</th>
                  <th>Produced</th>
                  <th>Expiry</th>
                  <th>Customer</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={8} className="text-center text-zinc-500 py-6">Loading…</td></tr>
                )}
                {!loading && pallets.length === 0 && (
                  <tr><td colSpan={8} className="text-center text-zinc-500 py-8">No pallets match your filters.</td></tr>
                )}
                {pallets.map((p) => (
                  <tr key={p.id} data-testid={`inv-row-${p.id}`}>
                    <td className="font-mono-id font-semibold">
                      <Link to={`/pallets/${p.id}`} className="hover:text-[var(--mj-red)]">
                        {p.pallet_number}
                      </Link>
                    </td>
                    <td>
                      <div className="font-semibold">{p.product_name}</div>
                      <div className="text-xs text-[var(--mj-ink-muted)] font-mono-id">{p.product_sku}</div>
                    </td>
                    <td className="font-mono-id">{p.lot_number}</td>
                    <td className="font-mono-id">{p.quantity}</td>
                    <td className="font-mono-id">{p.production_date}</td>
                    <td className="font-mono-id">{p.expiry_date}</td>
                    <td>{p.customer_name || "—"}</td>
                    <td><StatusPill status={p.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
